import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, scheduled, asyncScheduler } from 'rxjs';

import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';
import { EntityConstants } from '../../../../../shared/constants/entity.constants';

import { ToastService } from '../../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../../shared/services/error-service';

import { ColumnMapping, CreateColumnMappingInput, ImportColumnMappingService, ImportColumnService, PremiumDataBatchResult, PremiumDataService, WorkbookService } from '../../../../../../service/proxy';

@Component({
    selector: 'premium-data-column-selection',
    templateUrl: './premium-data-column-selection.component.html',
    styleUrls: ['./premium-data-column-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        PremiumDataService,
        ImportColumnService,
        ImportColumnMappingService,
        WorkbookService,
    ]
})

export class PremiumDataColumnSelectionComponent implements WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public headerIndex: number = 0;
    public batch: PremiumDataBatchResult = new PremiumDataBatchResult();

    public columnMappings: any[] = [];
    public databaseColumns: any[] = [];
    public validationMessage: string = '';

    constructor(
        private route: ActivatedRoute,
        private toastService: ToastService,
        private premiumDataService: PremiumDataService,
        private workbookService: WorkbookService,
        private importColumnService: ImportColumnService,
        private errorService: ErrorService
    ) {}

    async onActivate(): Promise<void> {
        this.route.paramMap
            .subscribe(async (params) => {
                this.batchId = Number(params.get('id'));

                if (this.batchId > 0) {
                    try {
                        const batch = await this.getBatchById();

                        await this.getHeaderIndex(this.batchId);
                        await this.loadDatabaseColumnHeaders();
                        await this.loadWorkbookColumnHeaders(batch);
                        await this.getImportColumnMapping();
                    }
                    catch (error) {
                        this.toastService.show('An error occurred while activating the component. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    private getBatchById(): Promise<PremiumDataBatchResult> {
        return new Promise((resolve, reject) => {
            this.premiumDataService.getPremiumDataBatchById(this.batchId)
                .subscribe({
                    next: (result) => {
                        this.batch = result;
                        this.getHeaderIndex(this.batchId);

                        resolve(result)
                    },
                    error: (error: any) => {
                        this.toastService.show('Failed to load batch data. Please try again', 'Error');
                        this.errorService.handleError(error);

                        reject(error);
                    }
                }
            );
        });
    }

    private getHeaderIndex(batchId: number): Promise<number> {
        return new Promise((resolve, reject) => {
            this.premiumDataService.getPremiumDataImportRangeMapping(batchId)
                .subscribe({
                    next: (result) => {
                        this.headerIndex = result?.headerIndex ?? 1;

                        resolve(this.headerIndex);
                    },
                    error: (error) => {
                        this.toastService.show('Failed to retrieve header row info.', 'Error');
                        this.errorService.handleError(error);

                        reject(error);
                    }
                }
            );
        });
    }

    private loadDatabaseColumnHeaders(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.importColumnService.getImportColumnsByEntityName(EntityConstants.PremiumData)
                .subscribe({
                    next: (columns) => {
                        // Remove any columns that are not relevant to mapping
                        this.databaseColumns = columns;

                        resolve();
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load database columns. Please try again.', 'Error');
                        this.errorService.handleError(error);

                        reject(error);
                    }
                }
            );
        });
    }

    private loadWorkbookColumnHeaders(batch: PremiumDataBatchResult): Promise<void> {
        return new Promise((resolve, reject) => {
            const filePath = `${batch.filePath}\\${batch.storedName}`;
            const sheetIndex = 0;

            this.workbookService.getColumnsBySheetIndex(filePath, sheetIndex, this.headerIndex)
                .subscribe({
                    next: (result) => {
                        this.columnMappings = result.headers.map((header: any, index: number) => ({
                            batchId: this.batchId,
                            columnIndex: index,
                            sourceColumnDescription: header.label || `Column ${index + 1}`,
                            destinationColumn: '',
                            formatString: '',
                            regex: '',
                            showAdvanced: false
                        }));

                        resolve();
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load headers from file. Please try again.', 'Error');
                        this.errorService.handleError(error);

                        reject(error);
                    }
                }
            );
        });
    }

    private getImportColumnMapping(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.premiumDataService.getPremiumDataImportColumnMapping(this.batchId)
                .subscribe({
                    next: (result: any) => {
                        if (!result || !result.mappings || result.mappings.length === 0) {
                            this.mapColumnSelectionsAutomatically();
                        }
                        else {
                            this.mapColumnSelectionsFromDatabase(result.mappings);
                        }

                        resolve();
                    },
                    error: (error: any) => {
                        this.toastService.show('Failed to load database columns. Please try again.', 'Error');
                        this.errorService.handleError(error);

                        reject(error);
                    }
                }
            );
        });
    }

    private mapColumnSelectionsAutomatically(): void {
        this.columnMappings.forEach(m => {
            const matchingDatabaseColumn = this.databaseColumns.find(dbCol =>
                dbCol.description.replace(' ', '').toLowerCase().includes(m['sourceColumnDescription'].replace(' ', '').toLowerCase())
            );

            if (matchingDatabaseColumn) {
                m.destinationColumn = matchingDatabaseColumn.name;
            }
        });
    }

    private mapColumnSelectionsFromDatabase(items: ColumnMapping[]): void {
        items.forEach((item: any) => {
            const row = this.columnMappings.find((m: any) => m.columnIndex === item.columnIndex);

            if (row) {
                row.destinationColumn = item.mappedPropertyName ?? row.destinationColumn;
                row.formatString = item.formatString ?? row.formatString;
                row.regex = item.regex ?? row.regex;
            }
        });
    }

    isValid(): boolean {
        // Don't validate until data is loaded
        if (!this.databaseColumns?.length || !this.columnMappings?.length) {
            return true; // Let it proceed if data isn't loaded yet
        }

        // Check for duplicate mappings
        const mappedFields = this.columnMappings
            .map(m => m.destinationColumn)
            .filter(Boolean);
        
        const duplicates = mappedFields.filter((field, index) => 
            mappedFields.indexOf(field) !== index
        );
        
        if (duplicates.length > 0) {
            const uniqueDuplicates = [...new Set(duplicates)];
            const fieldNames = uniqueDuplicates
                .map(fieldName => this.databaseColumns.find(col => col.name === fieldName)?.description || fieldName)
                .join(', ');
            this.validationMessage = `The following fields are mapped multiple times: ${fieldNames}. Each field can only be mapped once.`;
            return false;
        }

        // Check if all required database columns have been mapped
        const requiredColumns = this.databaseColumns.filter(col => col.required);
        const mappedColumns = this.columnMappings
            .filter(m => m.destinationColumn)
            .map(m => m.destinationColumn);
        
        const unmappedRequired = requiredColumns.filter(
            req => !mappedColumns.includes(req.name)
        );
        
        if (unmappedRequired.length > 0) {
            const fieldNames = unmappedRequired.map(col => col.description).join(', ');
            this.validationMessage = `Please map all required fields before proceeding. Missing: ${fieldNames}`;
            return false;
        }
        
        this.validationMessage = '';
        return this.form?.valid ?? true;
    }

    onMappingChange(): void {
        // Re-validate whenever a dropdown changes
        this.isValid();
    }

    onNext(): Observable<boolean> {
        if (!this.batchId || this.columnMappings.length === 0) {
            this.toastService.show('No column mappings to save.');
            return scheduled([false], asyncScheduler);
        }

        const payload = new CreateColumnMappingInput();

        payload.mappings = this.columnMappings.map(({ columnIndex, destinationColumn, formatString, regex }) => {
            const mapped = new ColumnMapping();

            mapped.columnIndex = columnIndex;
            mapped.mappedPropertyName = destinationColumn;
            mapped.formatString = formatString || undefined;
            mapped.regex = regex || undefined;

            return mapped;
        });

        return new Observable<boolean>(observer => {
            this.premiumDataService.createPremiumDataColumnMapping(this.batchId, payload)
                .subscribe({
                    next: () => {
                        this.toastService.show('Mappings saved successfully!', 'Success');

                        const mappingsChangedKey = `premium_data_mappings_changed_${this.batchId}`;
                        sessionStorage.setItem(mappingsChangedKey, 'true');

                        observer.next(true);
                        observer.complete();
                    },
                    error: (error: any) => {
                        this.toastService.show('Failed to save column mappings. Please try again.', 'error');
                        this.errorService.handleError(error);

                        observer.next(false);
                        observer.complete();
                    }
                }
            );
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }

    toggleAdvanced(mapping: any, event: Event): void {
        event.preventDefault();
        mapping.showAdvanced = !mapping.showAdvanced;
    }
}
